/**
 * Utilities for map listing pages (MyMaps, SharedMaps)
 */

export type ViewMode = 'list' | 'card';

const VIEW_MODE_STORAGE_KEY = 'mapListingViewMode';

/**
 * Filter maps by title using case-insensitive substring matching.
 * Handles special characters safely (no regex interpretation).
 *
 * @param maps - Array of map objects with a title property
 * @param searchTerm - Search string to filter by
 * @returns Filtered array of maps whose titles contain the search term
 */
export function filterMapsByTitle<T extends { title: string }>(
  maps: T[],
  searchTerm: string
): T[] {
  const trimmedSearch = searchTerm.trim();

  // Return all maps if search is empty
  if (!trimmedSearch) {
    return maps;
  }

  // Use lowercase comparison for case-insensitive matching
  // Avoid regex to prevent special character issues
  const lowerSearch = trimmedSearch.toLowerCase();

  return maps.filter((map) => map.title.toLowerCase().includes(lowerSearch));
}

/**
 * Get the user's preferred view mode from localStorage.
 * Defaults to 'card' if no preference is stored or if stored value is invalid.
 */
export function getViewModePreference(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === 'list' || stored === 'card') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (e.g., private browsing)
  }
  return 'card';
}

/**
 * Save the user's preferred view mode to localStorage.
 */
export function setViewModePreference(mode: ViewMode): void {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // localStorage may be unavailable (e.g., private browsing)
  }
}
